import React from 'react';
import { Container, Typography, Box, IconButton, Link, Divider } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate(-1)} aria-label="go back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Privacy Policy</Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Last updated: January 31, 2026
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>Information We Collect</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          When you create an account, we collect your username and email address. As you use the service, we store
          the grocery items, price records, shopping lists, and store information you choose to add.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          We collect anonymized analytics data including a hashed session fingerprint, device type, and browser
          information. This data does not contain any personally identifiable information (PII).
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          If you use our contact form, we collect the name, email, and message content you provide.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>How We Use Your Data</Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>To provide and operate the Grocery Price Checker service</li>
            <li>To improve the service through anonymized analytics</li>
            <li>To respond to messages submitted through the contact form</li>
          </ul>
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>Third-Party Services</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          We use the following third-party services:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><strong>Google Places API</strong> - for store location search and details</li>
            <li><strong>Open Food Facts</strong> - for product catalog data</li>
            <li><strong>Google reCAPTCHA</strong> - for spam protection on the contact form</li>
            <li><strong>AWS S3</strong> - for file storage</li>
            <li><strong>Sentry</strong> - for error tracking and monitoring</li>
          </ul>
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>Data Storage & Security</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          We take reasonable measures to protect your data. Authentication is handled via JSON Web Tokens (JWT).
          Passwords are securely hashed and never stored in plain text. Analytics fingerprints are hashed
          before storage. All communication with our servers is encrypted via HTTPS.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>Your Rights</Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>You may delete your account at any time from your Account page</li>
          </ul>
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>Children's Privacy</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Grocery Price Checker is not directed at children under 13. We do not knowingly collect personal
          information from children under 13. If you believe a child has provided us with personal data,
          please contact us so we can remove it.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>Changes to This Policy</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          We may update this Privacy Policy from time to time. Changes will be reflected by the "Last updated"
          date at the top of this page. Continued use of the service after changes constitutes acceptance
          of the updated policy.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>Contact</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          If you have questions about this Privacy Policy, please{' '}
          <Link component={RouterLink} to="/contact" underline="hover">contact us</Link>.
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
